import { useState } from "react";
import { ChevronDown, ChevronRight, FileText, Image as ImageIcon } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Блок вложений в user-сообщении.
 *
 * При отправке сообщения InputArea вставляет в текст:
 *   "<пользовательский текст>\n\n📎 file.pdf (file_id: abc)\n\n<полный markdown>"
 *   "🖼 photo.jpg:\n<vision/ocr описание>"
 *
 * Пользователь не должен видеть огромную простыню текста файла в собственном
 * сообщении: модель всё равно получает полный payload, а в чате достаточно
 * компактной карточки + сворачиваемого превью. Эта функция:
 *
 *   1. Находит блоки вида `📎 ... (file_id: <id>)\n\n<тело>` и `🖼 ...:\n<тело>`.
 *   2. Возвращает «чистый» пользовательский текст (без этих блоков).
 *   3. Возвращает массив чипов для рендера.
 */

interface AttachChip {
  icon: "file" | "image";
  name: string;
  fileId?: string;
  preview?: string;          // полный markdown / описание (для <details>)
}

interface SplitResult {
  cleanText: string;
  chips: AttachChip[];
}

const RE_FILE = /📎\s+([^\n()]+?)\s+\(\s*file_id\s*:\s*([\w-]+)\s*\)([\s\S]*?)(?=(?:\n+---\n+)?(?:📎|🖼|$))/g;
const RE_IMAGE = /🖼\s+([^\n:]+):\s*([\s\S]*?)(?=(?:\n+---\n+)?(?:📎|🖼|$))/g;

export function splitUserMessageAttachments(text: string): SplitResult {
  const chips: AttachChip[] = [];
  let cleaned = text;

  cleaned = cleaned.replace(RE_FILE, (_match, name: string, fid: string, body: string) => {
    chips.push({
      icon: "file",
      name: name.trim(),
      fileId: fid.trim(),
      preview: (body || "").trim(),
    });
    return "";
  });

  cleaned = cleaned.replace(RE_IMAGE, (_match, name: string, body: string) => {
    chips.push({
      icon: "image",
      name: name.trim(),
      preview: (body || "").trim(),
    });
    return "";
  });

  // Уборка остатков: лишних разделителей и пустых строк.
  cleaned = cleaned.replace(/(\n\s*---\s*\n)+/g, "\n").replace(/\n{3,}/g, "\n\n").trim();

  return { cleanText: cleaned, chips };
}

export function UserAttachmentChips({ chips }: { chips: AttachChip[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 mt-1.5">
      {chips.map((chip, i) => (
        <ChipItem key={`${chip.fileId || chip.name}-${i}`} chip={chip} />
      ))}
    </div>
  );
}

function ChipItem({ chip }: { chip: AttachChip }) {
  const [open, setOpen] = useState(false);
  const Icon = chip.icon === "image" ? ImageIcon : FileText;
  const hasPreview = !!chip.preview && chip.preview.length > 12;
  const previewText = hasPreview && chip.preview ? chip.preview : "";
  const truncated = previewText.length > 4000 ? previewText.slice(0, 4000) + "\n\n…(текст обрезан в превью; модель видит полностью)" : previewText;

  return (
    <div className="rounded-lg border border-white/15 bg-white/5 backdrop-blur-sm overflow-hidden">
      <button
        type="button"
        onClick={() => hasPreview && setOpen((v) => !v)}
        className={cn(
          "w-full px-2.5 py-1.5 flex items-center gap-2 text-left text-[0.78rem]",
          hasPreview ? "hover:bg-white/10 cursor-pointer" : "cursor-default",
        )}
        title={hasPreview ? "Показать содержимое, отправленное модели" : chip.name}
        aria-expanded={open}
      >
        <Icon className="size-3.5 shrink-0 text-white/85" />
        <span className="font-medium text-white truncate">{chip.name}</span>
        {chip.fileId && (
          <span className="ml-1 font-mono text-[0.7rem] text-white/55 truncate">
            {chip.fileId.slice(0, 8)}…
          </span>
        )}
        {hasPreview && (
          open
            ? <ChevronDown className="size-3.5 text-white/70 ml-auto shrink-0" />
            : <ChevronRight className="size-3.5 text-white/70 ml-auto shrink-0" />
        )}
      </button>
      {open && hasPreview && (
        <pre className="text-[0.72rem] leading-snug text-white/85 whitespace-pre-wrap p-2 max-h-64 overflow-auto border-t border-white/10 bg-black/15">
          {truncated}
        </pre>
      )}
    </div>
  );
}
